import type { ContactUserRestoration, ContactUserRestorationSkipReason } from '@app-types/admin';

const PLAIN_MESSAGE = 'User activated successfully';
const GENERIC_REASON = 'it could not be restored';

const SKIP_REASON_TEXT: Record<ContactUserRestorationSkipReason, string> = {
  USER_ID_NOT_RECORDED: 'the earlier UserID was not recorded',
  USER_ID_TAKEN: 'the earlier UserID is now held by another record',
  NO_RESTORABLE_ROLES: 'none of the earlier roles can be assigned any more',
  SOME_ROLES_NOT_RESTORABLE: 'some roles no longer exist or cannot be assigned'
};

const isSkipReason = (reason: string | undefined): reason is ContactUserRestorationSkipReason =>
  reason !== undefined && reason in SKIP_REASON_TEXT;

// The server may send a code this build does not know yet.
const getReasonText = (reason: string | undefined): string =>
  isSkipReason(reason) ? SKIP_REASON_TEXT[reason] : GENERIC_REASON;

/**
 * The success toast of an activation. The server gives back the roles and UserID the
 * deactivation removed when it can; the Admin is told what came back and what did not.
 */
export const getActivationMessage = (restored: ContactUserRestoration | undefined): string => {
  if (!restored) return PLAIN_MESSAGE;

  const reason = ` (${getReasonText(restored.reason)})`;
  if (restored.roles.length > 0) {
    const notRestored =
      restored.skipped_roles.length > 0 ? `. Not restored: ${restored.skipped_roles.join(', ')}${reason}` : '';
    return `User activated. Restored roles: ${restored.roles.join(', ')}; UserID: ${restored.user_id}${notRestored}`;
  }
  if (restored.skipped_roles.length > 0) {
    return `User activated. Roles and UserID could not be restored${reason} — assign them in Edit`;
  }
  return PLAIN_MESSAGE;
};
